import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import useImpersonation from '../../hooks/useImpersonation';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import EmptyState from '../../components/common/EmptyState';
import { SkeletonRow } from '../../components/common/Skeleton';
import {
  getTrainers, updateTrainerStatus,
  getTrainerById, updateTrainerNotes, updateTrainerSalary, deleteTrainer,
} from '../../services/trainerService';
import { sendPasswordReset } from '../../services/adminService';
import {
  MdPersonAdd, MdSearch, MdClose, MdCheckCircle, MdCancel,
  MdPerson, MdSchool, MdNotes, MdEdit, MdDelete, MdBlock, MdLockReset,
  MdEmail, MdPhone, MdCalendarToday, MdAttachMoney, MdChecklist, MdRemoveRedEye, MdExpandMore,
} from 'react-icons/md';
import { FaUserTie } from 'react-icons/fa';
import InviteModal from '../../components/common/InviteModal';
import EditTrainerModal from '../../components/trainers/EditTrainerModal';
import AvatarUploader from '../../components/AvatarUploader';
import PendingInvitationsPanel from '../../components/common/PendingInvitationsPanel';
import { useCalendar } from '../../context/CalendarContext';
import DualDate from '../../components/common/DualDate';
import useTheme from '../../context/ThemeContext';
import { getDisplayTxId } from '../../utils/displayId';
import SuspendedAccessPanel from '../../components/common/SuspendedAccessPanel';
import { isClubSuspended, isClubCancelled } from '../../utils/clubError';

const SPECIALIZATIONS = ['Taekwondo', 'Kickboxing', 'Judo', 'Karate', 'Boxing', 'Yoga', 'Fitness', 'Self-Defense'];
const DISCIPLINES     = ['Taekwondo', 'Kickboxing', 'Judo', 'Karate', 'Boxing', 'Yoga', 'Fitness'];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── Helpers ──────────────────────────────────────────────

function TrainerAvatar({ name, size = 'md', photoUrl }) {
  const sz = size === 'lg' ? 'w-14 h-14 text-xl' : 'w-9 h-9 text-sm';
  if (photoUrl) {
    return (
      <div className={`${sz} rounded-full overflow-hidden flex-shrink-0`}>
        <img src={photoUrl} alt={name || 'Avatar'} className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className={`${sz} bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold flex-shrink-0`}>
      {(name || '?')[0].toUpperCase()}
    </div>
  );
}

function TInfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-start gap-3 py-2 border-b border-divider last:border-0">
      <span className="text-xs text-subtle flex-shrink-0 w-28">{label}</span>
      <span className="text-sm text-secondary text-right">{value || '—'}</span>
    </div>
  );
}

function TSectionHead({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="text-primary text-base" />
      <h3 className="font-semibold text-secondary text-sm">{title}</h3>
    </div>
  );
}

// ── Trainer Detail Drawer ────────────────────────────────

function TrainerDrawer({ trainer, onClose }) {
  const queryClient = useQueryClient();
  const { isReadOnly } = useImpersonation();
  const currentTheme = useTheme()?.currentTheme;
  const [tab,           setTab]           = useState('profile');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen,      setEditOpen]      = useState(false);
  const [notesValue,    setNotesValue]    = useState('');
  const [notesInit,     setNotesInit]     = useState(false);
  const [editSalary,    setEditSalary]    = useState(false);
  const [salaryAmount,  setSalaryAmount]  = useState('');
  const [salaryFreq,    setSalaryFreq]    = useState('monthly');
  const [photoOverride, setPhotoOverride] = useState(null); // instant avatar update after upload

  const { data: profileData, isPending: loadingProfile } = useQuery({
    queryKey: ['trainer-full', trainer._id],
    queryFn:  () => getTrainerById(trainer._id).then(r => r.data.trainer),
    staleTime: 30000,
  });

  if (profileData && !notesInit) {
    setNotesValue(profileData.salary?.notes || '');
    setSalaryAmount(String(profileData.salary?.amount ?? ''));
    setSalaryFreq(profileData.salary?.frequency || 'monthly');
    setNotesInit(true);
  }

  const notesMutation = useMutation({
    mutationFn: (notes) => updateTrainerNotes(trainer._id, notes),
    onSuccess:  () => toast.success('Notes saved'),
    onError:    () => toast.error('Failed to save notes'),
  });

  const salaryMutation = useMutation({
    mutationFn: (data) => updateTrainerSalary(trainer._id, data),
    onSuccess: () => {
      toast.success('Salary updated');
      queryClient.invalidateQueries({ queryKey: ['trainer-full', trainer._id] });
      setEditSalary(false);
    },
    onError: () => toast.error('Failed to update salary'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ isActive }) => updateTrainerStatus(trainer._id, isActive),
    onSuccess: (_, { isActive }) => {
      toast.success(`Trainer ${isActive ? 'activated' : 'deactivated'}`);
      queryClient.invalidateQueries({ queryKey: ['trainers'] });
      queryClient.invalidateQueries({ queryKey: ['trainer-full', trainer._id] });
    },
    onError: () => toast.error('Failed to update status'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteTrainer(trainer._id),
    onSuccess: () => {
      toast.success('Trainer account deleted');
      queryClient.invalidateQueries({ queryKey: ['trainers'] });
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to delete'),
  });

  const resetMutation = useMutation({
    mutationFn: () => sendPasswordReset(profileData?.user?._id),
    onSuccess: () => toast.success('Password reset email sent'),
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to send reset email'),
  });

  const p   = profileData || trainer;
  const usr = p?.user || {};
  const hasEmail   = usr.email && !usr.email.includes('@noemail.club');
  const isActive   = usr.isActive;
  const classes    = profileData?.assignedClasses || [];
  const sessionStats = profileData?.sessionStats;

  const { formatDate } = useCalendar();

  const TABS = [
    { key: 'profile',  label: 'Profile'  },
    { key: 'classes',  label: 'Classes'  },
    { key: 'salary',   label: 'Salary'   },
    { key: 'admin',    label: 'Admin'    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-card h-full flex flex-col shadow-2xl relative">

        {/* Header */}
        <div className="flex-shrink-0 border-b border-divider">
          <div className="flex items-center justify-between px-5 py-3">
            <h2 className="font-bold text-secondary text-sm">Trainer Profile</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setEditOpen(true)}
                disabled={!profileData || isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : 'Edit Details'}
                className="h-8 px-2.5 flex items-center gap-1.5 rounded-lg text-sm font-medium text-secondary hover:text-primary hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdEdit className="text-base" /> Edit
              </button>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-subtle hover:bg-nav-hover">
                <MdClose className="text-xl" />
              </button>
            </div>
          </div>

          <div className="px-5 pb-4 flex items-center gap-4">
            <AvatarUploader
              currentPhotoUrl={photoOverride || p?.profilePhoto || null}
              studentName={usr.name}
              uploadUrl={`/api/trainers/${trainer._id}/photo`}
              size={64}
              onUploaded={(url) => {
                setPhotoOverride(url); // instant
                queryClient.invalidateQueries({ queryKey: ['trainer-full', trainer._id] });
                queryClient.invalidateQueries({ queryKey: ['trainers'] });
              }}
            />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-secondary truncate">{usr.name}</p>
              {hasEmail && <p className="text-xs text-subtle truncate">{usr.email}</p>}
              {usr.phone && <p className="text-xs text-subtle">{usr.phone}</p>}
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {p?.specializations?.slice(0, 2).map(s => (
                  <span key={s} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">{s}</span>
                ))}
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex border-t border-divider">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 py-2.5 text-xs font-semibold transition-colors border-b-2
                  ${tab === t.key ? 'border-primary text-primary' : 'border-transparent text-subtle hover:text-muted'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {loadingProfile && (
            <div className="p-5 space-y-3">
              {[1,2,3,4].map(i => <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />)}
            </div>
          )}

          {/* Profile Tab */}
          {!loadingProfile && tab === 'profile' && (
            <div className="p-5 space-y-5">
              <div>
                <TSectionHead icon={MdPerson} title="Personal Details" />
                <div>
                  <TInfoRow label="Full name"   value={usr.name} />
                  <TInfoRow label="Phone"       value={usr.phone} />
                  <TInfoRow label="Email"       value={hasEmail ? usr.email : null} />
                  <TInfoRow label="Experience"  value={p?.experienceYears ? `${p.experienceYears} year${p.experienceYears > 1 ? 's' : ''}` : null} />
                  <TInfoRow label="Joined"      value={<DualDate date={p?.joinDate} />} />
                </div>
              </div>

              {p?.specializations?.length > 0 && (
                <div>
                  <TSectionHead icon={MdSchool} title="Specializations" />
                  <div className="flex flex-wrap gap-2">
                    {p.specializations.map(s => (
                      <span key={s} className="text-xs bg-green-50 text-green-700 border border-green-100 px-3 py-1 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {p?.bio && (
                <div>
                  <TSectionHead icon={MdPerson} title="Bio" />
                  <p className="text-sm text-muted leading-relaxed">{p.bio}</p>
                </div>
              )}
            </div>
          )}

          {/* Classes Tab */}
          {!loadingProfile && tab === 'classes' && (
            <div className="p-5 space-y-4">
              {sessionStats && (
                <div className="bg-primary-light/40 rounded-xl p-3 flex gap-4">
                  <div className="text-center flex-1">
                    <p className="text-xl font-bold text-primary">{sessionStats.thisMonth}</p>
                    <p className="text-xs text-muted">Sessions this month</p>
                  </div>
                  <div className="w-px bg-gray-200" />
                  <div className="text-center flex-1">
                    <p className="text-sm font-semibold text-secondary">{sessionStats.lastSessionDate ? <DualDate date={sessionStats.lastSessionDate} /> : '—'}</p>
                    <p className="text-xs text-muted">Last session</p>
                  </div>
                </div>
              )}

              <div>
                <TSectionHead icon={MdSchool} title={`Assigned Classes (${classes.length})`} />
                {classes.length === 0 ? (
                  <div className="border border-dashed border-divider rounded-xl py-10 text-center">
                    <MdSchool className="text-3xl text-subtle mx-auto mb-2" />
                    <p className="text-sm text-subtle">No classes assigned yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {classes.map(cls => (
                      <div key={cls._id} className="border border-divider rounded-xl p-3 hover:border-primary/30 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-secondary">{cls.name}</p>
                            <p className="text-xs text-subtle mt-0.5">
                              {cls.days?.join(', ')}
                              {cls.startTime && ` · ${cls.startTime}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-xs bg-blue-50 text-blue-600 font-semibold px-2 py-0.5 rounded-full">
                              {cls.studentCount ?? 0} students
                            </span>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cls.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-muted'}`}>
                              {cls.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Salary Tab */}
          {!loadingProfile && tab === 'salary' && (
            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <TSectionHead icon={MdAttachMoney} title="Salary Details" />
                  {!editSalary && (
                    <button
                      onClick={() => setEditSalary(true)}
                      className="text-xs text-primary font-semibold flex items-center gap-1 hover:underline"
                    >
                      <MdEdit className="text-base" /> Edit
                    </button>
                  )}
                </div>

                {editSalary ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-muted mb-1">Amount (NPR)</label>
                      <input
                        type="number"
                        min="0"
                        value={salaryAmount}
                        onChange={e => setSalaryAmount(e.target.value)}
                        className="w-full px-3 py-2 border border-divider rounded-xl text-sm focus:outline-none focus:border-primary"
                        placeholder="e.g. 25000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted mb-1">Frequency</label>
                      <select
                        value={salaryFreq}
                        onChange={e => setSalaryFreq(e.target.value)}
                        className="w-full px-3 py-2 border border-divider rounded-xl text-sm bg-card focus:outline-none focus:border-primary"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="per_session">Per Session</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => setEditSalary(false)}
                        className="flex-1 py-2 border border-divider rounded-xl text-sm text-muted hover:bg-nav-hover"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => salaryMutation.mutate({ amount: Number(salaryAmount), frequency: salaryFreq })}
                        disabled={salaryMutation.isPending || isReadOnly}
                        title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                        className={`flex-1 py-2 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {salaryMutation.isPending ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <TInfoRow label="Amount"    value={p?.salary?.amount != null ? `NPR ${Number(p.salary.amount).toLocaleString()}` : null} />
                    <TInfoRow label="Frequency" value={p?.salary?.frequency?.replace(/_/g, ' ')} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Admin Tab */}
          {!loadingProfile && tab === 'admin' && (
            <div className="p-5 space-y-5">
              <div>
                <TSectionHead icon={MdNotes} title="Admin Notes" />
                <textarea
                  value={notesValue}
                  onChange={e => setNotesValue(e.target.value)}
                  onBlur={() => notesMutation.mutate(notesValue)}
                  rows={4}
                  placeholder="Private notes visible only to admins…"
                  className="w-full border border-divider rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary resize-none transition-colors"
                />
                <p className="text-xs text-subtle mt-1">Auto-saves when you click away</p>
              </div>

              <div>
                <TSectionHead icon={MdEdit} title="Actions" />
                <div className="space-y-2">
                  <button
                    onClick={() => resetMutation.mutate()}
                    disabled={!hasEmail || resetMutation.isPending || isReadOnly}
                    title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                    className="w-full flex items-center gap-2 px-4 py-2.5 border border-divider rounded-xl text-sm text-secondary hover:bg-nav-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <MdLockReset className="text-blue-500 text-lg" />
                    {resetMutation.isPending ? 'Sending…' : 'Send Password Reset Email'}
                    {!hasEmail && <span className="text-xs text-subtle ml-auto">No email on file</span>}
                  </button>
                  <button
                    onClick={() => statusMutation.mutate({ isActive: !isActive })}
                    disabled={statusMutation.isPending || isReadOnly}
                    title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                    className={`w-full flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                      ${isActive ? 'border-red-200 text-red-600 hover:bg-nav-hover' : 'border-green-200 text-green-600 hover:bg-nav-hover'}`}
                  >
                    {isActive ? <MdBlock className="text-lg" /> : <MdCheckCircle className="text-lg" />}
                    {statusMutation.isPending ? 'Updating…' : isActive ? 'Deactivate Account' : 'Activate Account'}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(true)}
                    disabled={isReadOnly}
                    title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                    className="w-full flex items-center gap-2 px-4 py-2.5 border border-red-200 rounded-xl text-sm text-red-600 hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <MdDelete className="text-lg" /> Delete Account Permanently
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Confirm delete overlay */}
        {confirmDelete && (
          <div className="absolute inset-0 bg-black/40 z-10 flex items-center justify-center p-6">
            <div className="bg-card rounded-2xl shadow-2xl p-6 w-full max-w-xs">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-3">
                <MdDelete className="text-red-600 text-2xl" />
              </div>
              <h3 className="font-bold text-secondary text-center mb-1">Delete Account?</h3>
              <p className="text-sm text-muted text-center mb-5">
                This permanently removes <strong>{usr.name}</strong> and all their data. This cannot be undone.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmDelete(false)} className="flex-1 py-2 border border-divider rounded-xl text-sm text-muted hover:bg-nav-hover">Cancel</button>
                <button
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending || isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {editOpen && profileData && (
          <EditTrainerModal
            trainer={profileData}
            onClose={() => setEditOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────

function Trainers() {
  document.title = 'Trainers — TrainAxis';
  const queryClient = useQueryClient();
  const { formatDate } = useCalendar();
  const { isReadOnly } = useImpersonation();
  const [page, setPage]               = useState(1);
  const [limit, setLimit]             = useState(10);
  const [sortBy, setSortBy]           = useState('createdAt');
  const [sortOrder, setSortOrder]     = useState('desc');
  const [search, setSearch]           = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [specFilter, setSpecFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [inviteOpen, setInviteOpen]   = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const debounceRef                   = useRef(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['trainers', page, limit, sortBy, sortOrder, search, specFilter, statusFilter],
    queryFn:  () => {
      // Only send params that have a value — never send empty-string filters.
      const params = { page, limit, sortBy, sortOrder };
      if (search)       params.search         = search;
      if (specFilter)   params.specialization = specFilter;
      if (statusFilter) params.isActive       = statusFilter;
      return getTrainers(params).then(r => r.data);
    },
    placeholderData: (prev) => prev,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }) => updateTrainerStatus(id, isActive),
    onSuccess:  () => {
      toast.success('Status updated');
      queryClient.invalidateQueries({ queryKey: ['trainers'] });
    },
    onError: () => toast.error('Failed to update status'),
  });

  const handleSearch = (val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val); setPage(1); }, 400);
  };

  // Robust parsing — tolerant of any response shape (handles deploy version skew)
  const trainers = data?.trainers || data?.data || (Array.isArray(data) ? data : []);
  const total    = data?.pagination?.total ?? data?.total ?? trainers.length;
  const pages    = data?.pagination?.totalPages ?? data?.pages ?? 1;

  // Sortable headers — same field flips direction, new field defaults to asc; resets page
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };
  const sortIcon = (field) => (sortBy !== field ? '↕' : sortOrder === 'asc' ? '↑' : '↓');

  // If deactivating empties the current page, step back one page
  useEffect(() => {
    if (!isPending && total > 0 && trainers.length === 0 && page > 1) {
      setPage(p => Math.max(1, p - 1));
    }
  }, [isPending, total, trainers.length, page]);

  // Club access blocked (subscription suspended/expired) — clear panel, not "0 trainers".
  if (isClubSuspended(error)) return <SuspendedAccessPanel />;
  if (isClubCancelled(error)) return <SuspendedAccessPanel cancelled />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-subtle text-sm">{total} trainer{total !== 1 ? 's' : ''} registered</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="inverted"
            onClick={() => setInviteOpen(true)}
            disabled={isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}>
            <MdPersonAdd className="text-lg" /> Invite Trainer
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[180px] relative">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle text-lg" />
            <input
              value={searchInput}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search by name, email, phone, or TrainAxis ID…"
              className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-divider rounded-full focus:outline-none focus:ring-2 focus:ring-divider"
            />
          </div>
          <div className="relative">
          <select
            value={specFilter}
            onChange={e => { setSpecFilter(e.target.value); setPage(1); }}
            className="appearance-none pl-4 pr-9 py-2 bg-card border border-divider rounded-full text-sm truncate focus:outline-none focus:ring-2 focus:ring-divider"
          >
            <option value="">All Specializations</option>
            {DISCIPLINES.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
          </div>
          <div className="relative">
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="appearance-none pl-4 pr-9 py-2 bg-card border border-divider rounded-full text-sm truncate focus:outline-none focus:ring-2 focus:ring-divider"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
          </div>
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="text-xs text-subtle">Rows</span>
            <div className="relative">
            <select
              value={limit}
              onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
              className="appearance-none pl-4 pr-9 py-2 bg-card border border-divider rounded-full text-sm truncate focus:outline-none focus:ring-2 focus:ring-divider"
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <MdExpandMore className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-subtle" />
            </div>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[560px]">
          <thead>
            <tr className="bg-gray-50 border-b border-divider text-xs text-subtle font-semibold uppercase tracking-wide">
              <th className="text-left px-5 py-3">
                <button onClick={() => toggleSort('fullName')} className="inline-flex items-center gap-1 hover:text-primary transition-colors">
                  Trainer <span className="text-[10px]">{sortIcon('fullName')}</span>
                </button>
              </th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Specializations</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Experience</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">
                <button onClick={() => toggleSort('createdAt')} className="inline-flex items-center gap-1 hover:text-primary transition-colors">
                  Joined <span className="text-[10px]">{sortIcon('createdAt')}</span>
                </button>
              </th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {isPending
              ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={6} />)
              : !trainers.length
                ? (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState
                        icon={FaUserTie}
                        title="No trainers added yet"
                        description="Add trainers so they can manage sessions and take attendance from their phone."
                        primaryAction={{ label: 'Invite Trainer', icon: MdPersonAdd, onClick: () => { if (!isReadOnly) setInviteOpen(true); } }}
                        secondaryAction={{ label: 'View Setup Checklist', icon: MdChecklist, to: '/admin/dashboard' }}
                      />
                    </td>
                  </tr>
                )
                : trainers.map(t => (
                  <tr key={t._id} onClick={() => setSelectedTrainer(t)} className="border-b border-divider last:border-0 hover:bg-nav-hover transition-colors cursor-pointer">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <TrainerAvatar name={t.user?.name} photoUrl={t.profilePhoto} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-secondary truncate">{t.user?.name}</p>
                          <span className="inline-block mt-0.5 text-[10px] font-mono font-semibold text-muted bg-gray-100 px-1.5 py-0.5 rounded">
                            {getDisplayTxId(t)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {t.specializations?.slice(0, 2).map(s => (
                          <span key={s} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">{s}</span>
                        ))}
                        {t.specializations?.length > 2 && (
                          <span className="text-xs text-subtle">+{t.specializations.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-sm text-muted">
                      {t.experienceYears ? `${t.experienceYears} yr${t.experienceYears > 1 ? 's' : ''}` : '—'}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted whitespace-nowrap">
                      <DualDate date={t.joinDate} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${t.user?.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {t.user?.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          title="View Profile"
                          onClick={(e) => { e.stopPropagation(); setSelectedTrainer(t); }}
                          className="w-8 h-8 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover hover:text-secondary transition-colors">
                          <MdRemoveRedEye className="text-lg" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); statusMutation.mutate({ id: t._id, isActive: !t.user?.isActive }); }}
                          disabled={statusMutation.isPending || isReadOnly}
                          className="w-8 h-8 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover hover:text-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          title={isReadOnly ? 'Disabled: read-only support mode' : (t.user?.isActive ? 'Deactivate trainer' : 'Activate trainer')}
                        >
                          {t.user?.isActive
                            ? <MdCancel className="text-lg" />
                            : <MdCheckCircle className="text-lg" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
        </div>

        {total > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-divider gap-3 flex-wrap">
            <p className="text-xs text-subtle">
              Showing {(page - 1) * limit + 1}–{(page - 1) * limit + trainers.length} of {total} · Page {page} of {pages}
            </p>
            <div className="flex gap-2">
              <Button variant="ghostPremium" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</Button>
              <Button variant="ghostPremium" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}>Next →</Button>
            </div>
          </div>
        )}
      </Card>

      <PendingInvitationsPanel role="trainer" />

      {inviteOpen && (
        <InviteModal lockedRole="trainer" onClose={() => setInviteOpen(false)} />
      )}

      {selectedTrainer && (
        <TrainerDrawer
          trainer={selectedTrainer}
          onClose={() => setSelectedTrainer(null)}
        />
      )}
    </div>
  );
}

export default Trainers;
